import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Modal, Typography, Spin, Button, message } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.css';
import api, { monthlyMetricService } from '../../../services/api';
import { formatPercentageValue, getCurrencySymbol } from '../utils/formatters';
import { getMetricColorStyle } from '../utils/colorUtils';
import MetricSelector from './MetricSelector';

const { Title } = Typography;

const NewSummaryTableModal = ({ 
  open,
  onCancel,
  selectedMetrics: initialSelectedMetrics = [],
  selectedMetricDescriptions: initialSelectedMetricDescriptions = [],
  selectedMonth,
  onMetricsChange,
  filteredBrands = [] // Filtreli markalar
}) => {
  const [loading, setLoading] = useState(false);
  const [allBrands, setAllBrands] = useState([]);
  const [monthlyMetricsData, setMonthlyMetricsData] = useState({});
  const [selectedMetrics, setSelectedMetrics] = useState(initialSelectedMetrics);
  const [selectedMetricDescriptions, setSelectedMetricDescriptions] = useState(initialSelectedMetricDescriptions);
  const [isMetricSelectorVisible, setIsMetricSelectorVisible] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Varsayılan metrikler (artık zorunlu değil, kullanıcı seçimi)
  const defaultMetricDescriptions = useMemo(() => [
    'TOPLAM CİRO',
    'CİRO HEDEF SAPMASI', 
    'AY SONU TAHMİNİ CİRO',
    'ÖNCEKİ AYA GÖRE TAHMİNİ DEĞİŞİM',
    'GÜNLÜK ORTALAMA CİRO',
    'GÜNLÜK GEREKEN CİRO',
    'ROI',
    'HEDEF ROI'
  ], []);

  // Mobile detection - window resize listener with debounce - SADECE MODAL AÇIKKEN
  useEffect(() => {
    if (!open) return; // Modal kapalıysa resize listener'ı aktif etme
    
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newIsMobile = window.innerWidth < 768;
        if (newIsMobile !== isMobile) {
          setIsMobile(newIsMobile);
        }
      }, 200); // Debounce süresini artırdık
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [open, isMobile]);

  // Props değiştiğinde local state'i güncelle - SADECE MODAL AÇIKKEN
  useEffect(() => {
    if (!open) return; // Modal kapalıysa hiçbir şey yapma
    
    // Sadece kaydedilmiş metrikleri yükle (default metrik otomatik eklenmez)
    let finalMetricDescriptions = [];
    let finalMetrics = [];
    
    // localStorage'dan kayıtlı metrik seçimlerini yükle
    if (initialSelectedMetricDescriptions && initialSelectedMetricDescriptions.length > 0) {
      finalMetricDescriptions = [...initialSelectedMetricDescriptions];
    }
    
    if (initialSelectedMetrics && initialSelectedMetrics.length > 0) {
      finalMetrics = [...initialSelectedMetrics];
    }
    
    // State'i güncelle
    setSelectedMetrics(finalMetrics);
    setSelectedMetricDescriptions(finalMetricDescriptions);
    
    // Parent component'e bildir - ama sadece değerler farklıysa
    if (onMetricsChange && 
        (JSON.stringify(finalMetrics) !== JSON.stringify(selectedMetrics) ||
         JSON.stringify(finalMetricDescriptions) !== JSON.stringify(selectedMetricDescriptions))) {
      onMetricsChange(finalMetrics, finalMetricDescriptions);
    }
  }, [open, initialSelectedMetrics, initialSelectedMetricDescriptions]);

  // Column widths'i memoize et
  const columnWidths = useMemo(() => {
    if (tableColumns.length === 0) return [isMobile ? 100 : 120];
    return [isMobile ? 100 : 120, ...Array(Math.max(0, tableColumns.length - 1)).fill('auto')];
  }, [isMobile, tableColumns.length]);

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (!open) return;
    
    const loadData = async () => {
      try {
        setLoading(true);
        
        // 1. Eğer filtrelenmiş markalar varsa onları kullan, yoksa tüm markaları yükle
        if (filteredBrands && filteredBrands.length > 0) {
          // Filtrelenmiş markalar kullanılacak
          setAllBrands(filteredBrands);
        } else {
          // Tüm markaları yükle
          const brandsResponse = await api.get('/brands', {
            params: {
              limit: 1000,
              offset: 0,
              orderBy: 'displayOrder',
              orderDirection: 'asc',
              includeInactive: 'true',
              date: selectedMonth ? selectedMonth.format('YYYY-MM') : null
            }
          });
          
          const brandsData = brandsResponse.data.sort((a, b) => {
            const orderA = a.displayOrder || 0;
            const orderB = b.displayOrder || 0;
            return orderA - orderB;
          });
          
          setAllBrands(brandsData);
        }
        
        // 2. Tüm aktif markaların aylık metriklerini yükle
        const formattedDate = selectedMonth.format('YYYY-MM');
        const monthlyData = await monthlyMetricService.getAllActiveBrandsMonthlyMetrics(formattedDate);
        setMonthlyMetricsData(monthlyData);
        
      } catch (error) {
        console.error('Modal verileri yüklenirken hata:', error);
        message.error('Veriler yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [open, selectedMonth, filteredBrands]);

  // Tablo verisi hazırla
  useEffect(() => {
    if (allBrands.length === 0) {
      setTableData([]);
      setTableColumns([]);
      return;
    }
    
    // selectedMetricDescriptions boşsa zorunlu metrikleri kullan
    const descriptionsToShow = selectedMetricDescriptions && selectedMetricDescriptions.length > 0 
      ? selectedMetricDescriptions 
      : []; // Hiçbir metrik seçili değilse boş liste

    try {
      // Sütunları hazırla
      const columns = [
        {
          data: 'brandName',
          type: 'text',
          readOnly: true,
          title: 'Marka'
        }
      ];

      // Her description için sütun ekle
      descriptionsToShow.forEach(description => {
        columns.push({
          data: `metric_${description}`,
          type: 'text',
          readOnly: true,
          title: description,
          metricDescription: description
        });
      });

      // Satırları hazırla
      const rows = allBrands.map(brand => {
        const row = {
          brandId: brand.id,
          brandName: brand.name
        };

        // Her metric description için değer bul
        descriptionsToShow.forEach(description => {
          const brandMetrics = monthlyMetricsData[brand.id]?.metrics || [];
          
          // Aynı description'a sahip tüm metrikleri bul
          const matchingMetrics = brandMetrics.filter(m => m.description?.trim() === description);
          
          // Değeri null/undefined olmayan ve 0 olmayan ilk metriği seç
          const validMetric = matchingMetrics.find(m => {
            const value = m.value;
            return value !== null && value !== undefined && value !== 0 && value !== '0';
          });
          
          if (validMetric) {
            row[`metric_${description}`] = validMetric.value;
          } else {
            // Eğer geçerli metrik bulunamazsa, ilk metriği dene (eski davranış)
            const firstMetric = matchingMetrics[0];
            if (firstMetric && firstMetric.value !== null && firstMetric.value !== undefined) {
              row[`metric_${description}`] = firstMetric.value;
            } else {
              row[`metric_${description}`] = null;
            }
          }
        });

        return row;
      });

      setTableColumns(columns);
      setTableData(rows);
    } catch (error) {
      console.error('Tablo oluşturma hatası:', error);
    }
  }, [selectedMetricDescriptions, allBrands, monthlyMetricsData]);

  // Metrik seçimi değiştirme
  const handleMetricsSave = useCallback((metrics, descriptions) => {
    const metricIds = Array.isArray(metrics) ? metrics : [];
    const metricDescriptions = Array.isArray(descriptions) ? descriptions : [];
    
    setSelectedMetrics(metricIds);
    setSelectedMetricDescriptions(metricDescriptions);
    setIsMetricSelectorVisible(false);
    
    // Parent component'e bildir
    if (onMetricsChange) {
      onMetricsChange(metricIds, metricDescriptions);
    }
  }, [onMetricsChange]);

  // Hücre render fonksiyonu
  const cellRenderer = (instance, td, row, col, prop, value, cellProperties) => {
    if (col === 0) {
      // Marka adı sütunu
      Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
      td.style.fontWeight = 'bold';
      td.style.backgroundColor = '#f5f5f5';
      td.style.fontSize = '12px'; // Font boyutunu küçült
      td.style.lineHeight = '1.2';
      td.style.whiteSpace = 'normal'; // Satır kırılmasına izin ver
      td.style.wordWrap = 'break-word';
      td.style.verticalAlign = 'top';
      td.style.padding = '4px';
      
      // Marka adını 2. kelimeden sonra böl
      if (value && typeof value === 'string') {
        const words = value.split(' ');
        if (words.length > 2) {
          const firstLine = words.slice(0, 2).join(' ');
          const secondLine = words.slice(2).join(' ');
          td.innerHTML = `<div style="font-size: 12px; line-height: 1.2; font-weight: bold;">${firstLine}<br/>${secondLine}</div>`;
        } else {
          td.innerHTML = `<div style="font-size: 12px; line-height: 1.2; font-weight: bold;">${value}</div>`;
        }
      }
      
      return td;
    }

    const column = tableColumns[col];
    if (!column) return td;

    const metricDescription = column.metricDescription;
    if (!metricDescription) {
      Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
      return td;
    }

    const brandId = tableData[row]?.brandId;
    if (!brandId) {
      Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
      return td;
    }

    // Değer null/undefined ise
    if (value === null || value === undefined || value === '') {
      td.textContent = '-';
      td.style.color = '#999';
      return td;
    }

    // Metrik bilgilerini al
    const brandMetrics = monthlyMetricsData[brandId]?.metrics || [];
    const metric = brandMetrics.find(m => m.description?.trim() === metricDescription);
    
    if (!metric) {
      td.textContent = '-';
      td.style.color = '#999';
      return td;
    }

    // Veri tipine göre formatla
    const dataType = metric.dataType || 'numeric';
    const brandCurrency = monthlyMetricsData[brandId]?.brandInfo?.currency || 'TRY';

    if (dataType === 'currency') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const currencySymbol = getCurrencySymbol(brandCurrency);
        td.textContent = Math.round(numValue).toLocaleString('tr-TR') + ' ' + currencySymbol;
      }
    } else if (dataType === 'percentage') {
      td.textContent = formatPercentageValue(value);
    } else if (dataType === 'text') {
      td.textContent = String(value).length > 42 ? String(value).substring(0, 42) + '...' : value;
      if (String(value).length > 42) {
        td.title = value;
      }
    } else {
      // Numeric
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        td.textContent = numValue.toLocaleString('tr-TR');
      } else {
        td.textContent = value;
      }
    }

    // Renklendirme için metrik bilgilerini al
    const brandMetricsForColoring = monthlyMetricsData[brandId]?.metrics || [];
    
    // Aynı description'a sahip tüm metrikleri bul
    const matchingMetricsForColoring = brandMetricsForColoring.filter(m => m.description?.trim() === metricDescription);
    
    // Değeri null/undefined olmayan ve 0 olmayan ilk metriği seç
    const currentMetric = matchingMetricsForColoring.find(m => {
      const value = m.value;
      return value !== null && value !== undefined && value !== 0 && value !== '0';
    }) || matchingMetricsForColoring[0]; // Bulunamazsa ilkini al
    
    if (currentMetric) {
      // 1. Genel renklendirme (colorUtils'deki özel metrikler)
      const colorStyle = getMetricColorStyle(currentMetric.id, value);
      
      if (colorStyle.backgroundColor) {
        td.style.backgroundColor = colorStyle.backgroundColor;
        td.style.color = colorStyle.color || '#000000';
        if (colorStyle.fontWeight) {
          td.style.fontWeight = colorStyle.fontWeight;
        }
      }
      
      // 2. ROI karşılaştırma renklendirmesi (10009 metriki)
      if (currentMetric.id === 10009) {
        // Önce 10008'i dene
        let compareMetric = brandMetricsForColoring.find(m => m.id === 10008);
        
        // Eğer 10008 null ise, TARGET ROI description'ına sahip metriği bul
        if (!compareMetric || compareMetric.value === null || compareMetric.value === undefined) {
          compareMetric = brandMetricsForColoring.find(m => 
            m.description?.trim().toLowerCase().includes('target roi') && 
            m.value !== null && 
            m.value !== undefined
          );
        }
        
        if (compareMetric && compareMetric.value !== null && compareMetric.value !== undefined) {
          const currentValue = parseFloat(value);
          const compareValue = parseFloat(compareMetric.value);
          
          if (!isNaN(currentValue) && !isNaN(compareValue)) {
            // ROI < Karşılaştırma metriği ise kırmızı, değilse yeşil
            td.style.backgroundColor = currentValue < compareValue ? 'rgba(107, 114, 128, 0.7)' : '#22c55e';
            td.style.color = '#ffffff';
            td.style.fontWeight = 'bold';
          }
        }
      }
      
      // 2.1. ROI karşılaştırma renklendirmesi (10063 metriki - TRENDYOL_GUNCEL_ROI)
      if (currentMetric.id === 10063) {
        // 10055 ile karşılaştır (TRENDYOL_GUNCEL_ROI vs TRENDYOL_HEDEF_ROI)
        const compareMetric = brandMetricsForColoring.find(m => m.id === 10055);
        
        if (compareMetric && compareMetric.value !== null && compareMetric.value !== undefined) {
          const currentValue = parseFloat(value);
          const compareValue = parseFloat(compareMetric.value);
          
          if (!isNaN(currentValue) && !isNaN(compareValue)) {
            // Trendyol ROI < Hedef ROI ise kırmızı, değilse yeşil
            td.style.backgroundColor = currentValue < compareValue ? 'rgba(107, 114, 128, 0.7)' : '#22c55e';
            td.style.color = '#ffffff';
            td.style.fontWeight = 'bold';
          }
        }
      }
    }

    return td;
  };

  // Modal kapatma
  const handleCancel = () => {
    setIsMetricSelectorVisible(false);
    onCancel();
  };

  return (
    <>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Title level={4} style={{ margin: 0 }}>Marka Metrikleri Özeti</Title>
            <div style={{ marginRight: '40px' }}>
              <Button
                type="primary"
                icon={<SettingOutlined />}
                onClick={() => setIsMetricSelectorVisible(true)}
                size="small"
              >
                Metrik Seç
              </Button>
            </div>
          </div>
        }
        open={open}
        onCancel={handleCancel}
        footer={null}
        width="95%"
        style={{ top: 20 }}
        destroyOnClose
        centered={false}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" tip="Veriler yükleniyor..." />
          </div>
        ) : selectedMetricDescriptions && selectedMetricDescriptions.length > 0 ? (
          <div style={{ height: 'calc(100vh - 200px)', overflowX: 'auto' }}>
            <HotTable
              key={`table-${isMobile}`}
              data={tableData}
              columns={tableColumns}
              rowHeaders={false}
              colHeaders={true}
              height="100%"
              width="100%"
              licenseKey="non-commercial-and-evaluation"
              stretchH="all"
              autoColumnSize={true}
              contextMenu={false}
              manualColumnResize={true}
              readOnly={true}
              fixedColumnsLeft={1}
              columnWidths={columnWidths}
              autoRowSize={true} // Otomatik satır yüksekliği - içeriğe göre ayarlanır
              cells={function() {
                const cellProps = {};
                cellProps.renderer = cellRenderer;
                return cellProps;
              }}
            />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Title level={4} type="secondary">
              Metrik Seçimi Yapılmamış
            </Title>
            <p>Lütfen görüntülenecek metrikleri seçin.</p>
            <Button
              type="primary"
              onClick={() => setIsMetricSelectorVisible(true)}
              style={{ marginTop: 16 }}
            >
              Metrik Seç
            </Button>
          </div>
        )}
      </Modal>

      {/* Metrik Seçici */}
      <MetricSelector
        open={isMetricSelectorVisible}
        onCancel={() => setIsMetricSelectorVisible(false)}
        onSave={handleMetricsSave}
        allMetricsData={{}}
        monthlyMetricsData={monthlyMetricsData}
        brands={allBrands}
        selectedMetrics={selectedMetrics}
        defaultMetricDescriptions={defaultMetricDescriptions}
      />
    </>
  );
};

export default NewSummaryTableModal;